import {z} from 'zod';

// ============================================================================
// Shared Constants
// ============================================================================

/**
 * The set of invariant check IDs supported by the reconciliation job.
 * Each ID maps to a specific drift mode defined in the dev plan.
 *
 * - I1: Role doc → roster entry present
 * - I2: Roster entry → role doc agrees
 * - I3: Active section leader has an appropriate role
 * - I4: SL role → has section assignment or permanent-covers entry
 * - I5: No `false` values in roster docs (tripwire for unknown writers)
 * - I6: Section members array ↔ user sections map are symmetric
 * - I7: Every role doc / roster entry references an existing user document
 * - I8: Confirmed-status flags match the user's actual role
 * - I9: Every roster doc name is in the canonical 8-role enum
 * - I10: User with role sl/mentor/student is missing the matching confirmed flag
 */
export const ALL_CHECK_IDS = [
  'I1',
  'I2',
  'I3',
  'I4',
  'I5',
  'I6',
  'I7',
  'I8',
  'I9',
  'I10',
] as const;

/** A single invariant check identifier. */
export type CheckId = (typeof ALL_CHECK_IDS)[number];

/** Zod schema for a check identifier. */
export const CheckIdSchema = z.enum(ALL_CHECK_IDS);

// ============================================================================
// Violation Schema
// ============================================================================

/**
 * Zod schema for a single invariant violation found during reconciliation.
 *
 * A violation records what was wrong, where it was found, how it was
 * classified, and (when auto-fixed) what the before/after state was.
 * The `before` and `after` fields are `unknown` because different checks
 * produce heterogeneous diff shapes; consumers should narrow with Zod
 * or type guards as needed.
 */
export const violationSchema = z.object({
  /**
   * Which invariant check detected this violation.
   */
  checkId: CheckIdSchema.describe(
    'The invariant check that detected this violation.',
  ),
  /**
   * The course in which the violation was found.
   */
  courseId: z.string().describe('The course in which the violation was found.'),
  /**
   * The affected user's Firebase Auth UID, when the violation is
   * user-scoped. Absent for course-level checks (e.g. I9).
   */
  userId: z
    .string()
    .optional()
    .describe(
      'The affected user UID, if the violation is user-scoped. Absent for course-level violations.',
    ),
  /**
   * The affected section ID, when the violation involves a section document
   * (e.g. I3, I4, I6).
   */
  sectionId: z
    .string()
    .optional()
    .describe(
      'The affected section ID, if the violation involves a section document.',
    ),
  /**
   * Snapshot of the relevant field(s) **before** any fix was applied.
   * `undefined` when the violation was not auto-fixed.
   */
  before: z
    .unknown()
    .optional()
    .describe(
      'Field value(s) before the fix, if the violation was auto-fixed.',
    ),
  /**
   * Snapshot of the relevant field(s) **after** the fix was applied.
   * `undefined` when the violation was not auto-fixed.
   */
  after: z
    .unknown()
    .optional()
    .describe('Field value(s) after the fix, if the violation was auto-fixed.'),
  /**
   * How this violation was handled:
   * - `'auto-fixed'`: the reconciliation job applied a safe, idempotent fix.
   * - `'needs-human'`: the violation requires a human editorial decision and
   *   was only reported, not mutated.
   * - `'informational'`: observed and surfaced for visibility, but not
   *   actionable. Reconciliation neither fixes nor flags it as a problem; it
   *   appears in the summary doc and structured logs only.
   */
  classification: z
    .enum(['auto-fixed', 'needs-human', 'informational'])
    .describe(
      "Either 'auto-fixed' (reconciliation corrected it), 'needs-human' (reported only), or 'informational' (surfaced for visibility only).",
    ),
  /**
   * A human-readable description of what was wrong and, for auto-fixed
   * violations, what was changed.
   */
  message: z
    .string()
    .describe(
      'Human-readable description of the violation and any fix applied.',
    ),
});

/**
 * TypeScript type inferred from {@link violationSchema}.
 * Represents a single invariant violation, optionally with before/after state.
 */
export type Violation = z.infer<typeof violationSchema>;

// ============================================================================
// Per-Check Counts Schema
// ============================================================================

/**
 * Zod schema for the violation counts attributed to a single invariant check.
 */
export const checkCountsSchema = z.object({
  /**
   * Number of violations that were automatically fixed in this run.
   */
  autoFixed: z
    .number()
    .int()
    .nonnegative()
    .describe('Count of violations auto-fixed by the reconciliation job.'),
  /**
   * Number of violations that require human review and were not mutated.
   */
  needsHuman: z
    .number()
    .int()
    .nonnegative()
    .describe('Count of violations that require human review.'),
  /**
   * Number of informational observations surfaced for visibility.
   * These are neither fixed nor flagged as actionable problems.
   */
  informational: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Count of informational observations surfaced for visibility only.',
    ),
});

/** TypeScript type inferred from {@link checkCountsSchema}. */
export type CheckCounts = z.infer<typeof checkCountsSchema>;

// ============================================================================
// Reconciliation Summary Document Schema
// ============================================================================

/**
 * Maximum number of {@link Violation} samples stored in the summary document
 * per invariant check. Violations beyond this limit are emitted only to
 * structured logs; the Firestore document stays bounded.
 */
export const SUMMARY_SAMPLE_LIMIT = 50;

/**
 * Zod schema for the daily reconciliation summary document stored at
 * `course/{courseId}/role-reconciliation/{YYYY-MM-DD}`.
 *
 * This document gives admins a queryable, per-course history of
 * reconciliation runs without requiring them to scan Cloud Logging.
 * The `samples` array is capped at {@link SUMMARY_SAMPLE_LIMIT} entries
 * per invariant check to keep the document size bounded.
 *
 * Complementary output: `storeHealthcheckRequest` with
 * `service: 'role-roster-reconciliation'` is emitted alongside each
 * summary doc for paging/alerting integration.
 *
 * The `mode` field was removed in PR 1.12. Reconciliation is detection-only
 * by design; every run is logically a dry-run. The field carried no
 * information and was removed rather than narrowed to `'dry-run'` for
 * clarity.
 */
export const reconciliationSummaryDocumentSchema = z.object({
  /**
   * ISO 8601 timestamp of when this reconciliation run started.
   * Example: `"2026-05-04T09:00:00.000Z"`.
   */
  ranAt: z.iso
    .datetime()
    .describe('ISO 8601 UTC timestamp of when the reconciliation run started.'),
  /**
   * Aggregate violation counts for this run.
   */
  counts: z
    .object({
      /**
       * Per-check breakdown of auto-fixed and needs-human counts.
       * Every check ID that was evaluated appears as a key. Not all check IDs
       * need be present (e.g. section checks are skipped for courses without
       * the `section` feature).
       */
      byCheck: z
        .record(z.string(), checkCountsSchema)
        .describe(
          'Per-check violation counts keyed by check ID (I1–I10). Only evaluated checks appear as keys.',
        ),
      /**
       * Total number of violations auto-fixed across all checks.
       */
      autoFixed: z
        .number()
        .int()
        .nonnegative()
        .describe('Total auto-fixed violations across all checks.'),
      /**
       * Total number of violations that require human review across all checks.
       */
      needsHuman: z
        .number()
        .int()
        .nonnegative()
        .describe('Total needs-human violations across all checks.'),
      /**
       * Total number of informational observations across all checks.
       */
      informational: z
        .number()
        .int()
        .nonnegative()
        .describe('Total informational observations across all checks.'),
    })
    .describe('Aggregate and per-check violation counts for this run.'),
  /**
   * Representative violation samples, capped at {@link SUMMARY_SAMPLE_LIMIT}
   * entries per invariant check. Violations beyond the cap appear only in
   * structured logs.
   */
  samples: z
    .array(violationSchema)
    .describe(
      'Representative violation samples (capped at SUMMARY_SAMPLE_LIMIT = 50 per check). Full detail is in Cloud Logging.',
    ),
});

/**
 * TypeScript type inferred from {@link reconciliationSummaryDocumentSchema}.
 * Represents the data shape of
 * `course/{courseId}/role-reconciliation/{YYYY-MM-DD}`.
 */
export type ReconciliationSummaryDocument = z.infer<
  typeof reconciliationSummaryDocumentSchema
>;

// ============================================================================
// Firestore Path Helpers
// ============================================================================

/** Top-level subcollection name for reconciliation data within a course. */
export const COURSE_RECONCILIATION_COLLECTION = 'role-reconciliation' as const;

/**
 * Returns the Firestore path for a daily role/roster reconciliation summary.
 *
 * Path: `course/{courseId}/role-reconciliation/{dateYyyyMmDd}`
 *
 * @param params - Path parameters.
 * @param params.courseId - The course identifier.
 * @param params.dateYyyyMmDd - The run date formatted as `YYYY-MM-DD`
 *   (e.g. `"2026-05-04"`). Using the UTC calendar date of the run start
 *   time is recommended so the path is deterministic regardless of timezone.
 * @return The absolute Firestore document path string.
 * @throws Never.
 */
export function getReconciliationSummaryDocumentPath({
  courseId,
  timestamp,
}: {
  courseId: string;
  timestamp: string;
}): string {
  return `course/${courseId}/${COURSE_RECONCILIATION_COLLECTION}/${timestamp}`;
}
